/**
 * Registration (สมัครสมาชิก) — ส่ง OTP และ verify OTP ผ่าน user-auth API
 */
import { getApiBaseUrl } from '../lib/apiBaseUrl'

const getBaseURL = () => getApiBaseUrl()

/** เบอร์ไทย 10 หลัก ขึ้นต้น 06, 08, 09 */
export function normalizeThaiMobile(value: string): string {
  const digits = value.replace(/\D/g, '')
  if (digits.length === 9 && /^[689]/.test(digits)) return '0' + digits
  if (digits.length === 10 && digits.startsWith('0')) return digits
  return digits.slice(0, 10)
}

export function isValidThaiMobile(mobile: string): boolean {
  return /^0[689]\d{8}$/.test(mobile.trim())
}

/** E.164 สำหรับ Firebase (เช่น +66644745270) */
export function toE164Thai(mobile: string): string {
  const n = normalizeThaiMobile(mobile)
  return n.startsWith('0') ? '+66' + n.slice(1) : '+66' + n
}

/** ดึง SMS config สาธารณะ (ไม่มี auth) — ใช้ตัดสินใจใช้ Firebase client หรือ backend */
export interface SMSConfigPublic {
  success: boolean
  data?: {
    smsEnabled: boolean
    primaryProvider?: string
    providers?: Array<{ type: string; priority?: number }>
  }
}

const normalizeSmsProviders = (providers: unknown): Array<{ type: string; priority?: number }> => {
  if (!Array.isArray(providers)) return []

  return providers
    .map((provider: any) => {
      const type = typeof provider?.type === 'string' ? provider.type.trim() : ''
      if (!type) return null

      return {
        type,
        priority: Number.isFinite(provider?.priority) ? provider.priority : 999,
      }
    })
    .filter(Boolean) as Array<{ type: string; priority?: number }>
}

export async function getSMSConfigPublic(tenantId: string): Promise<SMSConfigPublic['data']> {
  const baseURL = getBaseURL()
  const requestTs = Date.now()
  try {
    const res = await fetch(
      `${baseURL}/api/v1/configuration-service/tenant-sms/${tenantId}/providers/public?_t=${requestTs}`,
      {
        method: 'GET',
        headers: {
          'X-Tenant-ID': tenantId,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store'
      }
    )
    const payload = await res.json()
    const data = (payload?.data ? payload : { success: payload?.success, data: payload }) as SMSConfigPublic
    if (data?.success && data?.data) {
      const providers = normalizeSmsProviders(data.data.providers)

      return {
        ...data.data,
        primaryProvider: data.data.primaryProvider || providers[0]?.type || (data.data.smsEnabled ? 'firebase' : 'none'),
        providers,
      }
    }
  } catch {
    // ignore
  }
  return undefined
}

/** ตรวจสอบว่า tenant มี Firebase config หรือไม่ (ใช้เป็น fallback เมื่อ SMS config ไม่ชัด — ให้สอดคล้องกับ user-classic) */
export async function getFirebaseConfigAvailable(tenantId: string): Promise<boolean> {
  const baseURL = getBaseURL()
  try {
    const res = await fetch(
      `${baseURL}/api/v1/configuration-service/public/firebase-config/${tenantId}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json', 'X-Tenant-ID': tenantId } }
    )
    if (!res.ok) return false
    const json = await res.json()
    const config = json?.data ?? json
    return !!(config?.apiKey && config?.projectId)
  } catch {
    return false
  }
}

/** เช็ค cooldown การขอ OTP สมัครจาก backend — ดักที่ backend ไม่ให้ขอซ้ำ */
export interface CheckRegistrationOTPCooldownResult {
  success: boolean
  allowed: boolean
  remainingTimeMs?: number
  remainingTimeMin?: number
}

export async function checkRegistrationOTPCooldown(
  tenantId: string,
  mobile: string
): Promise<CheckRegistrationOTPCooldownResult> {
  const baseURL = getBaseURL()
  const normalized = normalizeThaiMobile(mobile)
  if (!isValidThaiMobile(normalized)) {
    return { success: true, allowed: true }
  }
  try {
    const res = await fetch(
      `${baseURL}/api/v1/user-auth/check-registration-otp-cooldown?tenantId=${encodeURIComponent(tenantId)}&mobile=${encodeURIComponent(normalized)}`,
      { method: 'GET', headers: { 'X-Tenant-ID': tenantId }, credentials: 'include' }
    )
    const data = (await res.json()) as CheckRegistrationOTPCooldownResult
    if (!res.ok) return { success: true, allowed: true }
    return data
  } catch {
    return { success: true, allowed: true }
  }
}

/** บันทึกว่าเพิ่งขอ OTP สมัครแล้ว (ใช้หลัง Firebase ส่ง OTP สำเร็จ) */
export async function recordRegistrationOTPSent(tenantId: string, mobile: string): Promise<{ success: boolean }> {
  const baseURL = getBaseURL()
  const normalized = normalizeThaiMobile(mobile)
  if (!isValidThaiMobile(normalized)) return { success: false }
  try {
    const res = await fetch(`${baseURL}/api/v1/user-auth/record-registration-otp-sent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Tenant-ID': tenantId },
      credentials: 'include',
      body: JSON.stringify({ tenantId, mobile: normalized }),
    })
    const data = (await res.json()) as { success?: boolean }
    return { success: data.success === true }
  } catch {
    return { success: false }
  }
}

/** ตรวจสอบว่าเบอร์มีในระบบแล้วหรือไม่ (ใช้ตอนกด ขอ OTP / ถัดไป) */
export interface CheckMobileAvailabilityResult {
  success: boolean
  available: boolean
  message?: string
  error?: string
}

export async function checkMobileAvailability(
  tenantId: string,
  mobile: string
): Promise<CheckMobileAvailabilityResult> {
  const baseURL = getBaseURL()
  const normalized = normalizeThaiMobile(mobile)
  if (!isValidThaiMobile(normalized)) {
    return { success: false, available: false, error: 'INVALID_MOBILE' }
  }
  try {
    const res = await fetch(
      `${baseURL}/api/v1/user-auth/check-mobile/${encodeURIComponent(normalized)}`,
      {
        method: 'GET',
        headers: { 'X-Tenant-ID': tenantId },
        credentials: 'include',
      }
    )
    const data = (await res.json()) as CheckMobileAvailabilityResult & { available?: boolean; nextStep?: string }
    if (!res.ok) {
      return {
        success: false,
        available: false,
        error: (data as { message?: string }).message || 'ไม่สามารถตรวจสอบเบอร์ได้',
      }
    }
    // เบอร์ "ใช้ได้" = ยังไม่มีในระบบ (available === true). เบอร์ซ้ำ = available === false หรือ nextStep === 'redirect_to_login'
    const available = data.available === true && data.nextStep !== 'redirect_to_login'
    return {
      success: true,
      available,
      message: data.message,
    }
  } catch (e) {
    return {
      success: false,
      available: false,
      error: e instanceof Error ? e.message : 'ไม่สามารถตรวจสอบเบอร์ได้',
    }
  }
}

export interface SendRegistrationOTPResult {
  success: boolean
  message?: string
  data?: {
    sessionId?: string
    mobile?: string
    expiresAt?: number
    /** มิลลิวินาทีที่ต้องรอก่อนกดขอ OTP ใหม่ (หลังส่งสำเร็จ) */
    canResendAfter?: number
  }
  error?: string
  /** เมื่อ 429 REGISTRATION_RATE_LIMIT_EXCEEDED */
  canResendAfter?: number
  remainingTimeMin?: number
}

/** Options when sending registration OTP (e.g. for Firebase reCAPTCHA) */
export interface SendRegistrationOTPOptions {
  recaptchaToken?: string
  provider?: string
  excludedProviders?: string[]
}

export async function sendRegistrationOTP(
  tenantId: string,
  mobile: string,
  options?: SendRegistrationOTPOptions
): Promise<SendRegistrationOTPResult> {
  const baseURL = getBaseURL()
  const normalized = normalizeThaiMobile(mobile)
  if (!isValidThaiMobile(normalized)) {
    return {
      success: false,
      message: 'เบอร์โทรศัพท์ไม่ถูกต้อง (ใช้ได้เฉพาะเบอร์ 10 หลัก ขึ้นต้น 06, 08, 09)',
      error: 'INVALID_MOBILE',
    }
  }
  const body: Record<string, string> = { tenantId, mobile: normalized }
  if (options?.recaptchaToken) body.recaptchaToken = options.recaptchaToken
  if (options?.provider) body.provider = options.provider
  if (options?.excludedProviders?.length) body.excludedProviders = JSON.stringify(options.excludedProviders)

  const res = await fetch(`${baseURL}/api/v1/user-auth/send-registration-otp`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-ID': tenantId,
    },
    body: JSON.stringify(body),
    credentials: 'include',
  })
  const data = (await res.json()) as SendRegistrationOTPResult & { errors?: unknown[]; data?: { canResendAfter?: number; remainingTimeMs?: number; remainingTimeMin?: number } }
  if (!res.ok) {
    const err = data as { error?: string; data?: { canResendAfter?: number; remainingTimeMs?: number; remainingTimeMin?: number } }
    return {
      success: false,
      message: data.message || 'ส่ง OTP ไม่สำเร็จ',
      error: err.error,
      canResendAfter: err.data?.canResendAfter ?? err.data?.remainingTimeMs,
      remainingTimeMin: err.data?.remainingTimeMin,
    }
  }
  return data
}

export interface VerifyRegistrationOTPResult {
  success: boolean
  message?: string
  data?: { phoneVerified: boolean; nextStep: string; sessionId?: string; verifiedAt?: string }
  error?: string
}

export async function verifyRegistrationOTP(
  tenantId: string,
  mobile: string,
  otpCode: string,
  sessionId: string
): Promise<VerifyRegistrationOTPResult> {
  const baseURL = getBaseURL()
  const normalized = normalizeThaiMobile(mobile)
  if (!isValidThaiMobile(normalized)) {
    return { success: false, message: 'เบอร์โทรศัพท์ไม่ถูกต้อง', error: 'INVALID_MOBILE' }
  }
  if (!/^\d{6}$/.test(otpCode)) {
    return { success: false, message: 'รหัส OTP ต้องเป็นตัวเลข 6 หลัก', error: 'INVALID_OTP' }
  }
  const res = await fetch(`${baseURL}/api/v1/user-auth/verify-registration-otp`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-ID': tenantId,
    },
    body: JSON.stringify({
      tenantId,
      mobile: normalized,
      otpCode,
      sessionId,
    }),
    credentials: 'include',
  })
  const data = (await res.json()) as VerifyRegistrationOTPResult
  if (!res.ok) {
    return {
      success: false,
      message: data.message || 'ยืนยัน OTP ไม่สำเร็จ',
      error: data.error,
    }
  }
  return data
}

/** ตรวจสอบว่าคู่ธนาคาร+หมายเลขบัญชีถูกใช้แล้วหรือยัง (ซ้ำในระบบให้ disable ปุ่ม) */
export async function checkBankAccountAvailability(
  tenantId: string,
  bankCode: string,
  accountNumber: string
): Promise<{ success: boolean; available: boolean; message?: string }> {
  const baseURL = getBaseURL()
  const code = (bankCode || '').trim()
  const num = (accountNumber || '').replace(/\D/g, '')
  if (!code || num.length < 10 || num.length > 12) {
    return { success: true, available: true }
  }
  try {
    const params = new URLSearchParams({ tenantId, bankCode: code, accountNumber: num })
    const res = await fetch(
      `${baseURL}/api/v1/user-auth/check-bank-account?${params}`,
      { method: 'GET', headers: { 'X-Tenant-ID': tenantId }, credentials: 'include' }
    )
    const data = (await res.json()) as { success?: boolean; available?: boolean; message?: string }
    if (!res.ok) return { success: false, available: true }
    return {
      success: true,
      available: data.available === true,
      message: data.message,
    }
  } catch {
    return { success: false, available: true }
  }
}

/** ตรวจสอบชื่อผู้ใช้ (Username) ใช้ซ้ำได้หรือไม่ — อย่างน้อย 6 ตัว อักษร/เลขอังกฤษเท่านั้น */
export interface CheckUsernameResult {
  success: boolean
  available?: boolean
  message?: string
  suggestions?: string[]
  error?: string
}

export async function checkUsername(tenantId: string, username: string): Promise<CheckUsernameResult> {
  const baseURL = getBaseURL()
  const trimmed = username.trim().toLowerCase()
  if (trimmed.length < 6) {
    return { success: false, available: false, error: 'Username อย่างน้อย 6 ตัวอักษร' }
  }
  if (!/^[a-zA-Z0-9]+$/.test(trimmed)) {
    return { success: false, available: false, error: 'ใช้ได้เฉพาะตัวอักษรอังกฤษและตัวเลข' }
  }
  try {
    const res = await fetch(
      `${baseURL}/api/v1/user-auth/check-username/${encodeURIComponent(trimmed)}`,
      { method: 'GET', headers: { 'X-Tenant-ID': tenantId }, credentials: 'include' }
    )
    const data = (await res.json()) as { success?: boolean; available?: boolean; message?: string; suggestions?: string[] }
    if (!res.ok) {
      return {
        success: false,
        available: false,
        error: (data as { message?: string }).message || 'ไม่สามารถตรวจสอบชื่อผู้ใช้ได้',
      }
    }
    return {
      success: true,
      available: data.available === true,
      suggestions: data.suggestions,
      message: data.message,
    }
  } catch (e) {
    return {
      success: false,
      available: false,
      error: e instanceof Error ? e.message : 'ไม่สามารถตรวจสอบชื่อผู้ใช้ได้',
    }
  }
}

/** สมัครสมาชิกสมบูรณ์หลังยืนยัน OTP + (ถ้ามี) ตั้งชื่อผู้ใช้ + ตั้งรหัสผ่าน (ใช้จาก ModernPasscodePage) */
export interface CompleteRegistrationResult {
  success: boolean
  message?: string
  data?: { fullName?: string; username?: string }
  error?: string
}

export interface CompleteRegistrationOptions {
  fullName?: string
  bankCode?: string
  accountNumber?: string
  bankImageUrl?: string
  referralCode?: string
}

export async function completeRegistration(
  tenantId: string,
  phoneNumber: string,
  password: string,
  sessionId: string,
  /** ถ้าไม่ส่ง จะใช้เบอร์โทรเป็น username */
  usernameOverride?: string,
  options?: CompleteRegistrationOptions
): Promise<CompleteRegistrationResult> {
  const baseURL = getBaseURL()
  const normalized = normalizeThaiMobile(phoneNumber)
  const e164 = normalized.startsWith('0') ? `+66${normalized.slice(1)}` : `+66${normalized}`
  const username = (usernameOverride && usernameOverride.trim()) ? usernameOverride.trim().toLowerCase() : normalized
  const body: Record<string, unknown> = {
    tenantId,
    phoneNumber: e164,
    username,
    password,
    sessionId,
    smsVerified: true,
    method: 'sms',
  }
  if (options?.fullName != null && options.fullName.trim() !== '') body.fullName = options.fullName.trim()
  if (options?.bankCode != null && options.bankCode.trim() !== '') body.bankCode = options.bankCode.trim()
  if (options?.accountNumber != null && options.accountNumber.trim() !== '') body.accountNumber = options.accountNumber.trim()
  if (options?.bankImageUrl != null && options.bankImageUrl.trim() !== '') body.bankImageUrl = options.bankImageUrl.trim()
  if (options?.referralCode != null && options.referralCode.trim() !== '') body.referralCode = options.referralCode.trim()

  const res = await fetch(`${baseURL}/api/v1/user-auth/complete-registration`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-ID': tenantId,
    },
    body: JSON.stringify(body),
    credentials: 'include',
  })
  const data = (await res.json()) as CompleteRegistrationResult & { message?: string }
  if (!res.ok) {
    return {
      success: false,
      message: data.message || 'สมัครสมาชิกไม่สำเร็จ',
      error: (data as { error?: string }).error,
    }
  }
  return data
}
